/** @module One card: picture, variants, Cardmarket value with its fields, own copies, reprints. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useRouter } from "@tanstack/react-router";
import { Check, ChevronLeft, ExternalLink, Pencil, Plus, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { type Item, ItemHistory, ItemSheet } from "@/components/item-sheet";
import { PageHeader } from "@/components/page-header";
import { AddCopySheet } from "@/features/catalog/add-copy-sheet";
import { marketPrice, PriceBox } from "@/features/catalog/price-box";
import { isFullArt, printedAs, printedNumber, useCard, variantLabel } from "@/lib/cards";
import { money } from "@/lib/format";

type Card = components["schemas"]["CardPrintOut"];

async function fetchCopies(printId: string) {
  const { data, response } = await api.GET("/api/v1/collection/items", {
    params: { query: { print_id: printId, limit: 200 } },
  });
  if (!data) throw problemFrom(response);
  return data.items;
}

async function fetchReprints(printId: string): Promise<Card[]> {
  const { data, response } = await api.GET("/api/v1/cards/{print_id}/reprints", {
    params: { path: { print_id: printId }, query: { limit: 24 } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** Cardmarket's own site languages; any other name searches the English site. */
const CARDMARKET_LANGUAGES = ["de", "en", "fr", "it", "es"];

/**
 * Cardmarket's search, which names its products "Olivinio (SVI 022)".
 *
 * Name, printed set code and number put the right card first (checked 2026-10-04); the site
 * language has to match the name's language, or the name finds nothing.
 */
export function cardmarketSearch(card: Card): string {
  const language = CARDMARKET_LANGUAGES.includes(card.name_lang) ? card.name_lang : "en";
  const query = [card.name, card.set_code, card.local_id].filter(Boolean).join(" ");
  const params = new URLSearchParams({ category: "-1", searchString: query, searchMode: "v2" });
  return `https://www.cardmarket.com/${language}/Pokemon/Products/Search?${params.toString()}`;
}

type Shared = components["schemas"]["SharedPrintOut"];

/** Why a price is held back; the reasons come from the checks after every price sync. */
function SuspiciousNote({ flags, shared }: { flags: string[]; shared: Shared[] }) {
  const { t } = useTranslation();
  // The cards behind a shared product id become links inside the sentence.
  const [before = "", after = ""] = t("price.flag.sharedWith", { cards: "\u0000" }).split("\u0000");
  return (
    <div className="callout callout-warn">
      <TriangleAlert />
      <span>
        <b>{t("price.suspiciousTitle")}</b>{" "}
        {flags.map((flag) =>
          flag === "shared_product_id" && shared.length > 0 ? (
            <span key={flag}>
              {before}
              {shared.map((card, index) => (
                <span key={card.print_id}>
                  {index > 0 ? ", " : ""}
                  <Link to="/cards/$printId" params={{ printId: card.print_id }} className="num">
                    {card.set_code ?? card.print_id} {card.local_id}
                  </Link>
                  {card.rarity ? ` (${card.rarity})` : ""}
                </span>
              ))}
              {after}{" "}
            </span>
          ) : (
            <span key={flag}>{t(`price.flag.${flag}`, t("price.flag.other"))} </span>
          ),
        )}
        {t("price.suspiciousTail")}
      </span>
    </div>
  );
}

function CopyPanel({ item, onEdit }: { item: Item; onEdit: () => void }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="panel panel-pad flex flex-col gap-[10px]">
      <div className="row-between">
        <span className="row">
          <span className="lang">{item.language.toUpperCase()}</span>
          <b className="num">{item.condition}</b>
        </span>
        <span className="num t-muted">
          {item.price?.suspicious
            ? `– ${t("collection.suspicious")}`
            : (money(item.value, i18n.resolvedLanguage ?? "de") ?? t("collection.unvalued"))}
        </span>
      </div>
      {(item.tags ?? []).length > 0 ? (
        <div className="flex flex-wrap gap-[6px]">
          {(item.tags ?? []).map((tag) => (
            <span key={tag.id} className="badge badge-neutral">
              {tag.name}
            </span>
          ))}
        </div>
      ) : null}
      {item.note ? <p className="t-sm t-muted">{item.note}</p> : null}
      <ItemHistory item={item} />
      <button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>
        <Pencil />
        {t("item.edit")}
      </button>
    </div>
  );
}

function Copies({ printId, onAdd }: { printId: string; onAdd: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const client = useQueryClient();
  const [editing, setEditing] = useState<Item | null>(null);
  const copies = useQuery({ queryKey: ["copies", printId], queryFn: () => fetchCopies(printId) });
  const items = copies.data ?? [];
  // A handful of copies of one card: summing the shown values is presentation, not valuation.
  const worth = items.reduce((sum, item) => sum + Number(item.value ?? 0), 0);

  return (
    <section className="flex flex-col gap-[14px]">
      <div className="row-between">
        <h2 className="t-h3">{t("catalog.yourCopies")}</h2>
        {items.length > 0 ? (
          <span className="num t-sm">
            {items.length}× · {money(worth, locale) ?? "–"}
          </span>
        ) : null}
      </div>
      {items.length === 0 ? (
        <div className="flex flex-col gap-3">
          <p className="t-sm t-muted">
            {copies.isPending ? t("common.loading") : t("catalog.noCopies")}
          </p>
          <button type="button" className="btn btn-secondary btn-sm self-start" onClick={onAdd}>
            <Plus />
            {t("addCopy.title")}
          </button>
        </div>
      ) : (
        <>
          {/* Phones: one chip per copy; a tap opens it. */}
          <div className="flex flex-wrap gap-[6px] lg:hidden">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="chip"
                onClick={() => {
                  setEditing(item);
                }}
              >
                <span className="lang">{item.language.toUpperCase()}</span>
                <span className="num">{item.condition}</span>
              </button>
            ))}
          </div>
          <div className="hidden flex-col gap-[14px] lg:flex">
            {items.map((item) => (
              <CopyPanel
                key={item.id}
                item={item}
                onEdit={() => {
                  setEditing(item);
                }}
              />
            ))}
          </div>
        </>
      )}
      {editing ? (
        <ItemSheet
          item={editing}
          showCardLink={false}
          onClose={() => {
            setEditing(null);
          }}
          onChanged={() => {
            setEditing(null);
            for (const key of ["copies", "collection", "dashboard", "set-progress"]) {
              void client.invalidateQueries({ queryKey: [key] });
            }
          }}
        />
      ) : null}
    </section>
  );
}

function Reprints({ printId }: { printId: string }) {
  const { t } = useTranslation();
  const reprints = useQuery({
    queryKey: ["reprints", printId],
    queryFn: () => fetchReprints(printId),
    staleTime: 10 * 60_000,
  });
  const cards = reprints.data ?? [];
  if (cards.length === 0) return null;
  return (
    <section className="flex flex-col gap-3">
      <h2 className="t-h3">{t("catalog.otherPrints")}</h2>
      <ul className="flex gap-3 overflow-x-auto pb-1">
        {cards.map((card) => (
          <li key={card.id} className="w-24 shrink-0">
            <Link
              to="/cards/$printId"
              params={{ printId: card.id }}
              className="tile text-(--text) no-underline"
            >
              <span className={`card-shape block ${isFullArt(card.rarity_class) ? "foil" : ""}`}>
                <CardFace printId={card.id} name={card.name} lazy />
              </span>
              <span className="tile-name num">{printedAs(card, card.id)}</span>
              <span className="t-xs t-muted truncate">{card.set_name}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CardPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { printId } = useParams({ strict: false });
  const card = useCard(printId ?? null);
  const [variantId, setVariantId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  if (!printId) return null;
  if (card.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (card.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const data = card.data;
  const variant = data.variants.find((one) => one.id === variantId) ?? data.variants[0];
  const price = marketPrice(variant, "cardmarket");
  const facts = [
    data.rarity,
    data.illustrator ? t("catalog.illustrator", { name: data.illustrator }) : null,
  ].filter(Boolean);
  const label = (key: string) => variantLabel(key, (k, fallback) => t(k, fallback));

  const header = (
    <PageHeader
      global={false}
      back={
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            // Coming from a link inside the app goes back there; a shared link goes home.
            if (window.history.length > 1) router.history.back();
            else void router.navigate({ to: "/collection" });
          }}
        >
          <ChevronLeft />
          {t("common.back")}
        </button>
      }
      actions={
        <>
          <a
            href={cardmarketSearch(data)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary btn-sm hidden md:inline-flex"
          >
            <ExternalLink />
            {t("catalog.onCardmarket")}
          </a>
          <button
            type="button"
            className="btn btn-primary btn-sm hidden md:inline-flex"
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus />
            {t("addCopy.title")}
          </button>
          <button
            type="button"
            className="icon-btn md:hidden"
            aria-label={t("addCopy.title")}
            onClick={() => {
              setAdding(true);
            }}
          >
            <Plus />
          </button>
        </>
      }
    />
  );

  return (
    <div className="flex flex-col gap-5 md:gap-7">
      {header}
      <div className="flex flex-col gap-[14px] lg:grid lg:grid-cols-[300px_minmax(0,1fr)_320px] lg:items-start lg:gap-7">
        <div className="mx-auto flex w-[200px] flex-col gap-[14px] lg:w-full">
          <span className={`card-shape block ${isFullArt(data.rarity_class) ? "foil" : ""}`}>
            <CardFace printId={data.id} name={data.name} quality="high" />
          </span>
          {data.image_lang ? (
            <span className="t-xs t-muted hidden text-center lg:block">
              {t("catalog.imageSource", { lang: data.image_lang.toUpperCase() })}
            </span>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-[14px] lg:gap-[18px]">
          <div className="flex flex-col gap-1">
            <h1 className="t-h2 lg:text-[length:var(--text-3xl)]">{data.name}</h1>
            <span className="t-sm t-muted lg:text-[length:var(--text-base)]">
              <Link to="/sets/$setId" params={{ setId: data.set_id }}>
                {data.set_name ?? data.set_id}
              </Link>
              {" · "}
              <span className="num">{printedNumber(data)}</span>
              {facts.map((fact) => ` · ${String(fact)}`).join("")}
            </span>
          </div>
          <div className="row flex-wrap gap-2">
            {data.variants.map((one) => (
              <button
                key={one.id}
                type="button"
                className="chip"
                aria-pressed={one.id === variant?.id}
                onClick={() => {
                  setVariantId(one.id);
                }}
              >
                {one.id === variant?.id ? <Check /> : null}
                {label(one.variant_key)}
              </button>
            ))}
            {data.variants.length === 1 ? (
              <span className="t-xs t-muted">{t("catalog.onlyVariant")}</span>
            ) : null}
          </div>
          {price?.suspicious ? (
            <SuspiciousNote flags={price.flags} shared={variant?.shares_product_with ?? []} />
          ) : null}
          {variant ? <PriceBox printId={data.id} variant={variant} /> : null}
          <a
            href={cardmarketSearch(data)}
            target="_blank"
            rel="noreferrer"
            className="btn btn-secondary btn-block md:hidden"
          >
            <ExternalLink />
            {t("catalog.onCardmarket")}
          </a>
        </div>

        <Copies
          printId={data.id}
          onAdd={() => {
            setAdding(true);
          }}
        />
      </div>
      <Reprints printId={data.id} />
      <AddCopySheet
        key={variant?.id}
        card={data}
        open={adding}
        initialVariant={variant?.id ?? null}
        onClose={() => {
          setAdding(false);
        }}
      />
    </div>
  );
}
